import {
  Banner,
  BlockStack,
  Button,
  InlineError,
  InlineGrid,
  InlineStack,
  Select,
  SelectGroup,
  SelectOption,
  Text,
  TextField,
} from '@shopify/polaris'
import { DeleteIcon } from '@shopify/polaris-icons'
import React, { useCallback, useMemo } from 'react'

import { allCurrencies, currencyPrefix, popularCurrencies } from '../common/currencies'
import { useShopCurrency } from '../common/Price'
import { useSessionStorage } from '../common/session-storage'
import {
  isWorkflowActionAnyCurrency,
  WORKFLOW_ACTION_ANY_CURRENCY,
} from '../common/workflow-models'

type PriceEntries = [string, string][]

interface Props {
  prices: Record<string, string>
  onChange: (prices: Record<string, string>) => void
  error: string | null
  index: number
  bannerText?: string
}

const unique = (codes: string[]) => codes.filter((code, i) => codes.indexOf(code) === i)

const currencyLabel = (code: string) => (isWorkflowActionAnyCurrency(code) ? 'any currency' : code)

/**
 * Editor of per-currency prices of a workflow action. The label, and the checkbox
 * of an optional price, are rendered by the action form.
 */
export const WorkflowActionPriceFields = ({
  prices,
  onChange,
  error,
  index,
  bannerText,
}: Props) => {
  const shopCurrency = useShopCurrency()

  const [isBannerDismissed, setBannerDismissed] = useSessionStorage<boolean>(
    `workflow-action-multi-currency-banner-dismissed-${index}`,
    false
  )

  // The "any currency" price is a fallback for the currencies listed above it, so it goes last
  const entries: PriceEntries = useMemo(
    () =>
      Object.entries(prices).sort(
        ([a], [b]) =>
          Number(isWorkflowActionAnyCurrency(a)) - Number(isWorkflowActionAnyCurrency(b))
      ),
    [prices]
  )
  const usedCodes = useMemo(() => entries.map(([code]) => code), [entries])
  const anyCurrencyAmount: string | undefined = prices[WORKFLOW_ACTION_ANY_CURRENCY]

  const popularCodes = useMemo(() => unique([shopCurrency, ...popularCurrencies]), [shopCurrency])

  const firstUnusedCode =
    unique([...popularCodes, ...allCurrencies]).find((code) => !usedCodes.includes(code)) ?? null

  // Options for one row: its own code plus every code not used by another row
  const optionsFor = (rowCode: string): (SelectOption | SelectGroup)[] => {
    const selectable = (code: string) => code === rowCode || !usedCodes.includes(code)
    const popular = popularCodes.filter(selectable)
    const all = allCurrencies.filter(selectable)

    if (
      !isWorkflowActionAnyCurrency(rowCode) &&
      !popular.includes(rowCode) &&
      !all.includes(rowCode)
    ) {
      popular.unshift(rowCode)
    }

    const toOptions = (codes: string[]) => codes.map((code) => ({ label: code, value: code }))
    const anyCurrency = selectable(WORKFLOW_ACTION_ANY_CURRENCY)
      ? [{ label: 'Any currency', value: WORKFLOW_ACTION_ANY_CURRENCY }]
      : []

    return [
      ...anyCurrency,
      { title: 'Popular currencies', options: toOptions(popular) },
      { title: 'All currencies', options: toOptions(all) },
    ]
  }

  const update = useCallback(
    (newEntries: PriceEntries) => onChange(Object.fromEntries(newEntries)),
    [onChange]
  )

  const setCode = (rowIndex: number, newCode: string) =>
    update(
      entries.map(([code, amount], i) => (i === rowIndex ? [newCode, amount] : [code, amount]))
    )

  const setAmount = (rowIndex: number, newAmount: string) =>
    update(
      entries.map(([code, amount], i) => (i === rowIndex ? [code, newAmount] : [code, amount]))
    )

  const removeRow = (rowIndex: number) => update(entries.filter((_, i) => i !== rowIndex))

  const addRow = () => firstUnusedCode && update([...entries, [firstUnusedCode, '']])

  return (
    <BlockStack gap="200">
      {entries.map(([code, amount], rowIndex) => (
        <InlineGrid key={code} columns={2} gap="200" alignItems="start">
          <Select
            label="Currency"
            labelHidden
            options={optionsFor(code)}
            value={code}
            onChange={(newCode: string) => setCode(rowIndex, newCode)}
          />
          <TextField
            label={`Price in ${currencyLabel(code)}`}
            labelHidden
            type="number"
            autoComplete="off"
            prefix={isWorkflowActionAnyCurrency(code) ? undefined : currencyPrefix(code)}
            value={amount}
            min="0"
            step={0.01}
            onChange={(val: string) => setAmount(rowIndex, val)}
            connectedRight={
              <Button
                icon={DeleteIcon}
                accessibilityLabel={`Remove ${currencyLabel(code)} price`}
                onClick={() => removeRow(rowIndex)}
                disabled={entries.length <= 1}
              />
            }
          />
        </InlineGrid>
      ))}
      {error && <InlineError message={error} fieldID="" />}
      <InlineStack>
        <Button size="slim" onClick={addRow} disabled={!firstUnusedCode}>
          Add currency
        </Button>
      </InlineStack>

      {bannerText && anyCurrencyAmount === undefined && !isBannerDismissed && (
        <Banner tone="info" onDismiss={() => setBannerDismissed(true)}>
          <Text as="p">{bannerText}</Text>
        </Banner>
      )}
      {anyCurrencyAmount !== undefined && parseFloat(anyCurrencyAmount) > 0 && (
        <Banner tone="warning">
          <Text as="p">
            The "Any currency" amount is set numerically as-is in the subscription's currency, for
            every currency that is not listed here. Be careful about applying it to subscriptions in
            different currencies.
          </Text>
        </Banner>
      )}
    </BlockStack>
  )
}
